import { useState } from 'react';
import { INVOICE_LINE_KINDS, VAT_TREATMENTS } from '@arcnet/contracts';
import type { Invoice } from '@arcnet/contracts';
import { useTenants } from '../../lib/api/hooks.js';
import { useSaveDraft, useDeleteDraft, useIssueInvoice } from '../../lib/api/hooks-invoices.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { formatMoney } from '../../lib/format.js';

/**
 * A draft invoice (docs/28 B2): its account, lines and notes. Saving works the
 * totals out at today's VAT rate; issuing numbers it, emails it and freezes it.
 * Both ask for a recent password check, as the API does.
 */

type Kind = (typeof INVOICE_LINE_KINDS)[number];
type Treatment = (typeof VAT_TREATMENTS)[number];
type LineForm = { kind: Kind; description: string; quantity: string; unit_price: string; vat_treatment: Treatment };

const blank = (): LineForm => ({ kind: 'other', description: '', quantity: '1', unit_price: '', vat_treatment: 'standard' });

const KIND_LABEL: Record<Kind, string> = {
  seat: 'Seat', number: 'Number', plan: 'Plan', add_on: 'Add-on', usage: 'Usage', adjustment: 'Adjustment', other: 'Other',
};
const VAT_LABEL: Record<Treatment, string> = { standard: 'Standard', zero_rated: 'Zero-rated', exempt: 'Exempt' };

const shown = (e: unknown): ApiClientError | null =>
  e instanceof ApiClientError && e.code !== 'auth.step_up_required' ? e : null;

export function InvoiceEditor({ draft, onClose, onIssued }: {
  /** Null starts a new one. */
  draft: Invoice | null;
  onClose: () => void;
  onIssued: (number: string) => void;
}) {
  const tenants = useTenants({ limit: 200 });
  const [draftId, setDraftId] = useState<string | null>(draft?.id ?? null);
  const save = useSaveDraft(draftId);
  const remove = useDeleteDraft();
  const issue = useIssueInvoice();
  const step = useStepUp();

  const [tenantId, setTenantId] = useState(draft?.tenant_id ?? '');
  const [reference, setReference] = useState(draft?.customer_reference ?? '');
  const [notes, setNotes] = useState(draft?.notes ?? '');
  const [lines, setLines] = useState<LineForm[]>(draft
    ? draft.lines.map((l) => ({
        kind: l.kind, description: l.description, quantity: l.quantity,
        unit_price: l.unit_price, vat_treatment: l.vat_treatment,
      }))
    : [blank()]);
  const [confirming, setConfirming] = useState(false);
  const saved = save.data ?? draft;

  const setLine = (n: number, patch: Partial<LineForm>) =>
    setLines((ls) => ls.map((l, i) => (i === n ? { ...l, ...patch } : l)));

  const persist = async (): Promise<Invoice> => {
    const result = await save.mutateAsync({
      tenant_id: tenantId,
      customer_reference: reference.trim() || null,
      notes: notes.trim() || null,
      lines: lines.map((l) => ({
        ...l, description: l.description.trim(), quantity: l.quantity.trim(), unit_price: l.unit_price.trim(),
      })),
    });
    setDraftId(result.id);
    return result;
  };

  // What is on screen is saved first, so what is issued is what was seen.
  const issueNow = () => void step.run(async () => {
    const current = await persist();
    const issued = await issue.mutateAsync(current.id);
    if (issued.number) onIssued(issued.number);
    return issued;
  });

  const err = shown(save.error) ?? shown(issue.error) ?? shown(remove.error);
  const busy = save.isPending || issue.isPending || remove.isPending;

  return (
    <div className="arc-card" style={{ marginBottom: 16 }}>
      <div className="arc-card-head">
        <h2 className="arc-h2">{draftId ? 'Draft invoice' : 'New invoice'}</h2>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={onClose}>Close</button>
      </div>
      <form className="arc-card-body" onSubmit={(e) => { e.preventDefault(); void step.run(persist); }}>
        <StepUpPrompt step={step} what="Changing an invoice" />
        {err && <ErrorBanner error={err} />}

        <div className="arc-grid arc-grid-2">
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="invoice-account">Account</label>
            <select id="invoice-account" className="arc-select" value={tenantId} required
              disabled={!!draftId} onChange={(e) => setTenantId(e.target.value)}>
              <option value="" disabled>Choose an account</option>
              {tenants.data?.data.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="invoice-reference">Their reference</label>
            <input id="invoice-reference" className="arc-input" value={reference}
              placeholder="A purchase order, if they gave one" onChange={(e) => setReference(e.target.value)} />
          </div>
        </div>

        <div className="arc-table-wrap" style={{ marginTop: 14 }}>
          <table className="arc-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: 120 }}>Kind</th>
                <th style={{ width: 90 }}>Quantity</th>
                <th style={{ width: 140 }}>Unit price excl. VAT</th>
                <th style={{ width: 130 }}>VAT</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, n) => (
                <tr key={n}>
                  <td>
                    <input className="arc-input" aria-label={`Line ${n + 1}: description`} required
                      value={l.description} onChange={(e) => setLine(n, { description: e.target.value })} />
                  </td>
                  <td>
                    <select className="arc-select" aria-label={`Line ${n + 1}: kind`} value={l.kind}
                      onChange={(e) => setLine(n, { kind: e.target.value as Kind })}>
                      {INVOICE_LINE_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                    </select>
                  </td>
                  <td>
                    <input className="arc-input arc-num" inputMode="decimal" required
                      aria-label={`Line ${n + 1}: quantity`} value={l.quantity}
                      onChange={(e) => setLine(n, { quantity: e.target.value })} />
                  </td>
                  <td>
                    <input className="arc-input arc-num" inputMode="decimal" required placeholder="0.00"
                      aria-label={`Line ${n + 1}: unit price excluding VAT`} value={l.unit_price}
                      onChange={(e) => setLine(n, { unit_price: e.target.value })} />
                  </td>
                  <td>
                    <select className="arc-select" aria-label={`Line ${n + 1}: VAT`} value={l.vat_treatment}
                      onChange={(e) => setLine(n, { vat_treatment: e.target.value as Treatment })}>
                      {VAT_TREATMENTS.map((t) => <option key={t} value={t}>{VAT_LABEL[t]}</option>)}
                    </select>
                  </td>
                  <td>
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                      aria-label={`Remove line ${n + 1}`} disabled={lines.length === 1}
                      onClick={() => setLines((ls) => ls.filter((_, i) => i !== n))}>
                      <Icon name="close" size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" style={{ marginTop: 8 }}
          onClick={() => setLines((ls) => [...ls, blank()])}>
          <Icon name="plus" size={13} /> Add a line
        </button>

        <div className="arc-field" style={{ marginTop: 14 }}>
          <label className="arc-label" htmlFor="invoice-notes">Notes printed on the invoice</label>
          <textarea id="invoice-notes" className="arc-input" rows={2} value={notes}
            onChange={(e) => setNotes(e.target.value)} />
        </div>

        {saved && (
          <p className="arc-muted" style={{ margin: '0 0 14px' }}>
            As saved: {formatMoney(saved.subtotal, saved.currency)} excluding VAT,
            {' '}{formatMoney(saved.total, saved.currency)} with VAT at today&apos;s rate.
          </p>
        )}

        <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button type="submit" className="arc-btn" disabled={busy}>
            {save.isPending && !issue.isPending ? 'Saving…' : 'Save draft'}
          </button>
          <button type="button" className="arc-btn arc-btn-primary" disabled={busy || !tenantId} onClick={issueNow}>
            {issue.isPending ? 'Issuing…' : 'Issue'}
          </button>
          <div className="arc-spacer" />
          {draftId && (confirming ? (
            <>
              <span className="arc-muted">Delete this draft?</span>
              <button type="button" className="arc-btn arc-btn-ghost" onClick={() => setConfirming(false)}>Keep</button>
              <button type="button" className="arc-btn arc-btn-danger" disabled={busy}
                onClick={() => void step.run(() => remove.mutateAsync(draftId).then(onClose))
                  .catch(() => undefined)}>
                Delete
              </button>
            </>
          ) : (
            <button type="button" className="arc-btn arc-btn-ghost" onClick={() => setConfirming(true)}>
              Delete draft
            </button>
          ))}
        </div>
        <p className="arc-hint" style={{ marginTop: 10 }}>
          Issuing numbers the invoice, emails it to the account&apos;s billing addresses and makes it
          final. A correction after that is a credit note.
        </p>
      </form>
    </div>
  );
}
